import { useEffect, useState, type InputHTMLAttributes } from "react";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "min" | "max"> & {
  /** null means empty, allowed only when `allowEmpty` is set. */
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  allowEmpty?: boolean;
};

/**
 * A number box without the browser's up/down arrows. People type freely (they can clear it and type a new number);
 * the value is checked when they leave the field: out-of-range numbers snap to the nearest limit, and an empty box
 * goes back to the last valid number unless `allowEmpty` is set.
 */
export default function NumberField({ value, onChange, min, max, allowEmpty, onBlur, onKeyDown, ...rest }: Props) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));

  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  const commit = () => {
    const digits = draft.replace(/[^\d]/g, "");
    if (!digits) {
      if (allowEmpty) onChange(null);
      else setDraft(value == null ? "" : String(value));
      return;
    }
    let n = parseInt(digits, 10);
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    setDraft(String(n));
    onChange(n);
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={draft}
      onChange={e => setDraft(e.target.value.replace(/[^\d]/g, ""))}
      onBlur={e => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={e => {
        if (e.key === "Enter") commit();
        onKeyDown?.(e);
      }}
    />
  );
}
